import { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CheckboxField, FormFieldProps } from '@brighte/form-components';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import {
  ApplicationEntityType,
  FINANCIAL_PRODUCT,
  IdentityType,
  createIdentificationFormSchema,
} from '@brighte/validation';
import { AddressWithSameAsCheckbox } from '@components/Address';
import { ConfirmFileUpload, FileUpload } from '@components/FileUpload';
import { StepNavigator } from '@components/StepNavigator';
import { ApplicationDocumentType } from '@graphql/index';
import { useUserDetails } from '@hooks';
import { useApplicationData } from '@provider';
import { Alert } from '@spark-web/alert';
import { Button } from '@spark-web/button';
import { InformationCircleIcon } from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import {
  APPLICATION_STEPS,
  IDENTIFICATION,
  LABELS,
  getAdminUserId,
  getApplicantTypeAndTrackingIdFromUrl,
  getFormFields,
  isFieldExists,
  isJointApplicant,
} from '@utils';
import IdentificationForm from './IdentificationForm';
import { useIdentificationSummary } from './hooks';

function Identification() {
  const {
    TITLE,
    DESCRIPTION,
    SAME_ADDRESS,
    PROOF_ID_DESCRIPTION,
    EXISTING_USER_NOTE,
    SKIP_DOCUMENTS_WARNING,
    IS_CONCESSION_CARD_HOLDER,
  } = IDENTIFICATION;

  const { HOME_ADDRESS, PROOF_ID } = LABELS;

  const {
    applicationId,
    Finance: finance,
    Identification: identification,
    quote,
    application,
  } = useApplicationData(['applicationId', 'Finance', 'Identification', 'quote', 'application']);

  const { financialProduct } = quote || {};
  const financialProductId = financialProduct?.id || '';
  const [isUploadHidden, setIsUploadHidden] = useState<boolean>(true);
  const { applicantType } = getApplicantTypeAndTrackingIdFromUrl();

  const { verificationCompleted } = useAuth0();

  const { data, refetch, isFetched } = useUserDetails({}, { enabled: false });
  // The query is disabled and only ever runs through the refetch below, so react-query's own
  // pending flags never settle on their own — gate on a fetch actually being due instead.
  // The form must not mount before that first response lands: it would seed react-hook-form with
  // empty defaults, and the arriving response then changes the key and remounts it, discarding
  // anything the customer had already typed. A failed fetch still counts as settled, so a profile
  // error leaves them with an editable form rather than blocking the step.
  const isAwaitingUserDetails = verificationCompleted && !isFetched;
  const isFpSynced = !!data?.fpSynced;
  const identificationFormSchema = useMemo(() => createIdentificationFormSchema(isFpSynced), [isFpSynced]);

  const isJoint = isJointApplicant();

  const sourceAddress = isJoint ? application?.address : finance?.address;

  useEffect(() => {
    if (verificationCompleted) {
      refetch();
    }
  }, [verificationCompleted]);

  /**
   * Pre-populate user data based on:
   * - If it's a saved application, use data from the application.
   * - Otherwise, use data from the Me query.
   *
   * Additionally, if the user data from the Me query is finPower synced (fpSynced has a date),
   * pre-populate and disable the following fields:
   * - firstName
   * - middleName
   * - lastName
   * - dateOfBirth
   */
  const identificationValues = identification?.applicant.firstName
    ? identification
    : {
        ...identification,
        applicant: {
          ...identification?.applicant,
          ...data,
        },
      };
  // `Form` seeds react-hook-form once, so `defaultValues` and the resolver are only picked up on
  // mount. The gate above means the Me response has already landed by the time the form mounts, so
  // this key stays put in the usual flow; it still has to cover the sync state, which selects both
  // the schema and the disabled state, for the case where saved application data arrives later.
  const identificationFormKey = identification?.applicant.firstName
    ? 'saved-identification'
    : data
      ? `user-details-${isFpSynced}`
      : 'empty-identification';

  return (
    <StepNavigator<IdentityType>
      key={identificationFormKey}
      schema={identificationFormSchema}
      name={APPLICATION_STEPS.IDENTIFICATION}
      title={TITLE}
      defaultValues={identificationValues || {}}
      isLoading={isAwaitingUserDetails}
      completedContent={useIdentificationSummary()}
    >
      {(formContext: UseFormReturn<IdentityType>) => {
        const { control, watch, setValue, register } = formContext;
        const { errors } = formContext.formState;
        const identificationType = watch('applicant.identification.type');

        const showDocumentUpload =
          financialProductId === FINANCIAL_PRODUCT.ELECTRIFY_2515 ? watch('applicant.isConcessionCardHolder') : true;

        const additionalFields = getFormFields(identificationFormSchema, financialProductId);
        const uploadedDocuments = watch('documents') ?? [];
        const isSkipDocuments = watch('isSkipDocuments') ?? false;

        return (
          <Stack gap="xxlarge">
            {isFpSynced && (
              <Text>
                {data.firstName}, {EXISTING_USER_NOTE}
              </Text>
            )}

            <Stack marginY="small">
              <Text>{DESCRIPTION}</Text>
            </Stack>

            <IdentificationForm
              control={control}
              watch={watch}
              setValue={setValue}
              name="applicant"
              isExistingUser={isFpSynced}
            />
            {additionalFields && isFieldExists(additionalFields, 'applicant.isConcessionCardHolder') ? (
              <CheckboxField
                name="applicant.isConcessionCardHolder"
                label={IS_CONCESSION_CARD_HOLDER}
                control={control}
              />
            ) : null}
            {identificationType ? (
              <>
                {showDocumentUpload && isFieldExists(additionalFields, 'documents') ? (
                  <>
                    {uploadedDocuments.length > 0 && isUploadHidden ? (
                      <>
                        <Button prominence="low" onClick={() => setIsUploadHidden(false)}>
                          Provide additional proof
                        </Button>
                        <ConfirmFileUpload confirmationText={`${PROOF_ID} provided`} />
                        <input type="hidden" {...register('documents')} />
                      </>
                    ) : (
                      <>
                        <FileUpload
                          applicationId={applicationId ?? ''}
                          title={PROOF_ID}
                          description={PROOF_ID_DESCRIPTION(financialProductId)}
                          fieldName="documents"
                          setFieldValue={setValue}
                          onFileSubmit={() => setIsUploadHidden(true)}
                          existingUploadedDocuments={uploadedDocuments}
                          documentType={ApplicationDocumentType.Identification}
                          applicantType={ApplicationEntityType[applicantType]}
                          error={errors?.documents}
                        />
                        {getAdminUserId() ? (
                          <Button tone="neutral" onClick={() => setValue(`isSkipDocuments`, true)}>
                            Skip document upload for now
                          </Button>
                        ) : null}
                      </>
                    )}
                  </>
                ) : null}
                {isSkipDocuments ? (
                  <Alert tone="caution" icon={InformationCircleIcon}>
                    {SKIP_DOCUMENTS_WARNING}
                  </Alert>
                ) : null}
                <AddressWithSameAsCheckbox
                  name="applicant.address"
                  sourceAddress={sourceAddress}
                  formContext={formContext as FormFieldProps<IdentityType>}
                  fieldLabel={HOME_ADDRESS}
                  checkboxLabel={SAME_ADDRESS}
                  checkboxName="isSameAddress"
                />
              </>
            ) : (
              <input type="hidden" {...register('isSameAddress')} />
            )}

            <input type="hidden" {...register('financialProductId')} value={financialProduct?.id ?? ''} />
            <input type="hidden" {...register('applicant.type')} value={applicantType} />
          </Stack>
        );
      }}
    </StepNavigator>
  );
}

export default Identification;
